/**
 * Zod Factories — Shared Validation Building Blocks
 *
 * Centralised Zod factories for the most common form fields (person name,
 * company, description, URL, etc.). Use these in any frontend form schema so
 * that:
 *
 * 1. **Length limits live in ONE place** — `VALIDATION_LIMITS` is the single
 *    source of truth for character caps. If product asks to bump
 *    "description" from 500 to 1000, you change it here and every modal
 *    inherits the new cap.
 * 2. **Error messages are i18n keys, NOT English strings** — every factory
 *    takes an `i18nPrefix` and emits keys like `"<prefix>.required"` and
 *    `"<prefix>.tooLong"`. The CONSUMING component is responsible for
 *    resolving those keys with `useTranslation` (Zod is synchronous + outside
 *    React, so it cannot call hooks itself).
 *
 * Pattern in consuming code:
 *
 * ```ts
 * import { personNameField } from '@bat/shared'
 *
 * const schema = z.object({
 *   firstName: personNameField('forms.user.firstName'),
 * })
 *
 * // In the component:
 * const errorMessage = t(form.formState.errors.firstName?.message)
 * ```
 */

import { z } from 'zod/v4'

import { PASSWORD_MIN_LENGTH } from '../../platform/validation/common.js'

// ============================================================
// LENGTH LIMITS — single source of truth
// ============================================================

/**
 * Character length limits used by `*Field` factories below.
 *
 * Keep these aligned with backend Zod validators (`packages/backend` and
 * `packages/shared/src/platform/validation/common.ts`). Bumping a limit here
 * MUST be mirrored backend-side or requests will start failing 400 after
 * passing client-side validation.
 */
export const VALIDATION_LIMITS = {
  /** Person first/last name max length (matches `nameValidation` in platform). */
  PERSON_NAME_MAX: 50,
  /** Company / organisation display name. */
  COMPANY_NAME_MAX: 100,
  /** Asset display name (admin asset registry). */
  ASSET_NAME_MAX: 100,
  /** Generic "description" textarea (rate-limit groups, backups, policies…). */
  DESCRIPTION_MAX: 500,
  /** Short free-text fields (titles, single-line labels). */
  SHORT_TEXT_MAX: 100,
  /** Long-form text body. */
  LONG_TEXT_MAX: 2000,
  /** External identifier strings (coingecko ID, CMC ID, OAuth client id). */
  ID_MAX: 100,
  /** Slug / canonical-id style (lowercase, underscores/hyphens). */
  SLUG_MAX: 50,
  /** Contact / support form person name (min 2 chars, max 100). */
  CONTACT_NAME_MIN: 2,
  CONTACT_NAME_MAX: 100,
  /** Notification broadcast name (admin-defined, longer than short text). */
  BROADCAST_NAME_MAX: 200,
} as const

// PASSWORD_MIN_LENGTH is re-exported below from `platform/validation/common.ts`
// so consumers can import everything from `@bat/shared/contracts/validation`
// without having to know the internal path.
export { PASSWORD_MIN_LENGTH } from '../../platform/validation/common.js'

// ============================================================
// STRING FIELD FACTORIES
// ============================================================

/**
 * Required person name (first or last) — `min(1)` + `max(PERSON_NAME_MAX)`.
 *
 * Emits two i18n keys: `${prefix}.required` and `${prefix}.tooLong`.
 */
export const personNameField = (i18nPrefix: string): z.ZodString =>
  z
    .string()
    .min(1, `${i18nPrefix}.required`)
    .max(VALIDATION_LIMITS.PERSON_NAME_MAX, `${i18nPrefix}.tooLong`)

/**
 * Optional person name — same cap, no `min(1)`.
 */
export const optionalPersonNameField = (i18nPrefix: string): z.ZodOptional<z.ZodString> =>
  z.string().max(VALIDATION_LIMITS.PERSON_NAME_MAX, `${i18nPrefix}.tooLong`).optional()

/**
 * Optional company / organisation name.
 */
export const companyNameField = (i18nPrefix: string): z.ZodOptional<z.ZodString> =>
  z.string().max(VALIDATION_LIMITS.COMPANY_NAME_MAX, `${i18nPrefix}.tooLong`).optional()

/**
 * Required asset display name.
 */
export const assetNameField = (i18nPrefix: string): z.ZodString =>
  z
    .string()
    .min(1, `${i18nPrefix}.required`)
    .max(VALIDATION_LIMITS.ASSET_NAME_MAX, `${i18nPrefix}.tooLong`)

/**
 * Optional free-text description (e.g. for backups, retention policies,
 * endpoint groups). Capped at `DESCRIPTION_MAX`.
 */
export const descriptionField = (i18nPrefix: string): z.ZodOptional<z.ZodString> =>
  z.string().max(VALIDATION_LIMITS.DESCRIPTION_MAX, `${i18nPrefix}.tooLong`).optional()

/**
 * Required short text field.
 */
export const shortTextField = (i18nPrefix: string): z.ZodString =>
  z
    .string()
    .min(1, `${i18nPrefix}.required`)
    .max(VALIDATION_LIMITS.SHORT_TEXT_MAX, `${i18nPrefix}.tooLong`)

/**
 * Required email field — combines presence + RFC email check.
 */
export const emailField = (i18nPrefix: string): z.ZodString =>
  z.string().min(1, `${i18nPrefix}.required`).email(`${i18nPrefix}.invalidEmail`)

/**
 * Required URL field.
 */
export const urlField = (i18nPrefix: string): z.ZodString =>
  z.string().url(`${i18nPrefix}.invalidUrl`)

/**
 * Optional URL field that ALSO accepts the empty string (so unchecked
 * URL inputs in forms don't trigger "invalid URL" errors when blank).
 */
export const optionalUrlField = (i18nPrefix: string) =>
  z.string().url(`${i18nPrefix}.invalidUrl`).optional().or(z.literal(''))

/**
 * Optional external identifier string (coingecko id, CMC id, OAuth client id).
 */
export const idField = (i18nPrefix: string): z.ZodOptional<z.ZodString> =>
  z.string().max(VALIDATION_LIMITS.ID_MAX, `${i18nPrefix}.tooLong`).optional()

/**
 * Required generic non-empty string ("X is required").
 *
 * Use this for select / dropdown / radio inputs where the only constraint is
 * "must be picked". For free-text fields with length limits prefer the
 * specific `*NameField` factories.
 */
export const requiredStringField = (i18nPrefix: string): z.ZodString =>
  z.string().min(1, `${i18nPrefix}.required`)

/**
 * Contact / support form name field — `min(CONTACT_NAME_MIN)` +
 * `max(CONTACT_NAME_MAX)`. Used in contact forms where we want at least 2
 * characters (rejects single-letter placeholders) but allow long full names
 * (up to 100 chars).
 */
export const contactNameField = (i18nPrefix: string): z.ZodString =>
  z
    .string()
    .min(VALIDATION_LIMITS.CONTACT_NAME_MIN, `${i18nPrefix}.tooShort`)
    .max(VALIDATION_LIMITS.CONTACT_NAME_MAX, `${i18nPrefix}.tooLong`)

/**
 * Notification broadcast name field — `min(1)` + `max(BROADCAST_NAME_MAX)` +
 * alphanumeric/whitespace/`-`/`_` only. Length is wider than `shortTextField`
 * because admin-defined broadcast labels can include verbose descriptors.
 */
export const broadcastNameField = (i18nPrefix: string): z.ZodString =>
  z
    .string()
    .min(1, `${i18nPrefix}.required`)
    .max(VALIDATION_LIMITS.BROADCAST_NAME_MAX, `${i18nPrefix}.tooLong`)
    .regex(/^[a-zA-Z0-9_\s-]+$/, `${i18nPrefix}.invalidFormat`)

// ============================================================
// PASSWORD FACTORIES
// ============================================================
//
// Two factories with deliberately DIFFERENT semantics:
//
// * `passwordFieldForLogin` — `min(1)`. We only enforce "field is non-empty"
//   client-side. The server (which knows the user's hashed password) is the
//   authoritative validator. Enforcing `min(8)` here would either (a) reject
//   pre-existing accounts whose passwords were created before the policy
//   changed, or (b) leak the current policy to attackers.
//
// * `passwordFieldForCreation` — `min(PASSWORD_MIN_LENGTH)`. Used everywhere a
//   NEW password is being set (signup, reset, admin create-user). This is the
//   single source of truth for "what counts as a strong-enough password" on
//   the client side. The full strength check (uppercase/lowercase/symbol/etc.)
//   still happens server-side in `PASSWORD_REQUIREMENTS`.

/**
 * Login password field — just verifies the input box has been filled in.
 *
 * Do NOT use this for new-password fields; use `passwordFieldForCreation`.
 */
export const passwordFieldForLogin = (i18nPrefix: string): z.ZodString =>
  z.string().min(1, `${i18nPrefix}.required`)

/**
 * New / reset password field — enforces `PASSWORD_MIN_LENGTH` minimum.
 *
 * Backend still performs the full strength check (uppercase, lowercase,
 * number, special character) in `password.validator.ts`.
 */
export const passwordFieldForCreation = (i18nPrefix: string): z.ZodString =>
  z.string().min(PASSWORD_MIN_LENGTH, `${i18nPrefix}.tooShort`)
